import {
  CellValueChangedEvent,
  ColDef,
  ExcelExportParams,
  FilterChangedEvent,
  IFilterOptionDef,
  RowDataTransaction,
  RowModelType,
  RowSelectionOptions,
  SelectionColumnDef,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import constate from 'constate';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ServerSideTotalRowCount } from 'core/components/AgGridTable/elements';
import { CellRendererForAgGrid } from 'core/elements';
import { AgGridFilterOption, TableLayoutType } from 'core/enums';
import {
  useAgGridExcelExport,
  useAgGridFilter,
  useAgGridLayout,
  useAgGridServerSideDatasource,
} from 'core/hooks';
import {
  AgGridCellValueChangedResult,
  AgGridDataRow,
  AgGridGetRowsParams,
  AgGridTransactionOptions,
  GetManyResponse,
} from 'core/types';
import {
  applyTrimmedTextFilterModel,
  getCustomDateFilter,
  ICustomGridOptionsParams,
  useGetGridOptions,
} from 'core/utils';

import { useAgGridCellUpdateContext } from './agGridCellUpdate';
import { AgGridTableProvider, useAgGridTableContext } from './agGridTable';

interface IProps extends Omit<ICustomGridOptionsParams, 'isRowSelectable' | 'onCellValueChanged'> {
  tableId: string;
  entityType: string;
  columnDefs: ColDef[];
  getRows: (params: AgGridGetRowsParams) => Promise<GetManyResponse<AgGridDataRow>>;
  onCellValueChanged?: (
    agGrid: AgGridReact,
    event: CellValueChangedEvent
  ) => Promise<AgGridCellValueChangedResult> | AgGridCellValueChangedResult;
  layoutType?: TableLayoutType;
  hasLayoutActions?: boolean;
  hasManageFilters?: boolean;
  hasQuickSearch?: boolean;
  hasTopActions?: boolean;
  hasRowSelection?: boolean;
  hasStatusBar?: boolean;
  selectionMode?: 'multiRow' | 'singleRow';
  isRowSelectable?: (params: any) => boolean;
  headerCheckbox?: boolean;
  enableClickSelection?: boolean;
  excelExportParams?: Partial<ExcelExportParams>;
}

const cellStyle = {
  alignContent: 'center',
  lineHeight: '1.2em',
};

const baseDefaultColDef = {
  sortable: true,
  minWidth: 120,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  resizable: true,
  width: null,
  pinned: null,
  hide: null,
  floatingFilter: true,
  filterParams: {
    buttons: ['reset', 'apply'],
    closeOnApply: true,
    closeOnReset: true,
    inRangeInclusive: true,
  },
  cellRenderer: CellRendererForAgGrid,
  cellStyle,
};

const getSupportedFilterOptions = (filter: string): (string | IFilterOptionDef)[] => {
  switch (filter) {
    case 'agNumberColumnFilter':
      return [
        AgGridFilterOption.EQUALS,
        AgGridFilterOption.IN_RANGE,
        AgGridFilterOption.NOT_EQUAL,
        AgGridFilterOption.LESS_THAN,
        AgGridFilterOption.LESS_THAN_OR_EQUAL,
        AgGridFilterOption.GREATER_THAN,
        AgGridFilterOption.GREATER_THAN_OR_EQUAL,
      ];
    case 'agTextColumnFilter':
      return [
        AgGridFilterOption.CONTAINS,
        AgGridFilterOption.NOT_CONTAINS,
        AgGridFilterOption.EQUALS,
        AgGridFilterOption.NOT_EQUAL,
        AgGridFilterOption.STARTS_WITH,
        AgGridFilterOption.ENDS_WITH,
        AgGridFilterOption.BLANK,
        AgGridFilterOption.NOT_BLANK,
      ];
    case 'agDateColumnFilter':
      return [
        AgGridFilterOption.EQUALS,
        AgGridFilterOption.LESS_THAN,
        AgGridFilterOption.GREATER_THAN,
        AgGridFilterOption.IN_RANGE,
        ...getCustomDateFilter(),
      ];
    default:
      return undefined;
  }
};

const useAgGridServerTable = ({
  tableId,
  entityType,
  columnDefs,
  getRows,
  onCellValueChanged,
  pageSize = 25,
  layoutType,
  hasLayoutActions = false,
  hasManageFilters = false,
  defaultColDef: customDefaultColDef,
  selectionColumnDef: customSelectionColumnDef,
  initialState: initialStateProp,
  statusBar: customStatusBar,
  context: extraContext = {},
  hasQuickSearch = true,
  hasTopActions = true,
  hasRowSelection = true,
  hasStatusBar = true,
  selectionMode = 'multiRow',
  isRowSelectable = null,
  headerCheckbox = false,
  enableClickSelection = false,
  excelExportParams,
  ...rest
}: IProps) => {
  const { gridRef, gridApi, selectedRows, onSelectionChanged, deselectAllRows, onRowsUpdated } =
    useAgGridTableContext();

  const [totals, setTotals] = useState<Record<string, number>>({});

  const { t } = useTranslation();

  const { setUpdating } = useAgGridCellUpdateContext();

  const columnDefsWithDefaultFilterOptions = useMemo(
    () =>
      columnDefs.filter(Boolean).map(({ filter, filterParams, ...rest }) => ({
        ...rest,
        filter,
        filterParams: {
          filterOptions: getSupportedFilterOptions(filter),
          // Custom React filter components render their own Reset/Apply buttons.
          // Suppress AG-Grid's native action buttons (inherited from defaultColDef)
          // so they aren't injected on top, producing a duplicate apply panel.
          ...(typeof filter === 'function' && { buttons: [] }),
          ...(filter === 'agTextColumnFilter' && { trimInput: true }),
          ...filterParams,
        },
      })),
    [columnDefs]
  );

  const defaultColDef = useMemo(
    () => ({ ...baseDefaultColDef, ...customDefaultColDef }),
    [customDefaultColDef]
  );

  const selectionColumnDef: SelectionColumnDef = useMemo(
    () => ({
      minWidth: 40,
      width: 40,
      maxWidth: 40,
      suppressColumnsToolPanel: true,
      suppressMovable: true,
      lockPosition: true,
      headerClass: 'paddingSmall center',
      cellClass: 'paddingSmall center noDecorationCell',
      pinned: 'left',
      lockPinned: true,
      ...customSelectionColumnDef,
    }),
    [customSelectionColumnDef]
  );

  const { serverSideDatasource } = useAgGridServerSideDatasource({
    gridRef,
    pageSize,
    getRows,
    setTotals,
  });

  const { defaultCsvExportParams, defaultExcelExportParams } = useAgGridExcelExport({
    gridApi,
    excelExportParams,
  });

  const { initialState, saveFilterState, defaultFilterModel } = useAgGridFilter({
    layoutType,
    initialState: initialStateProp,
    hasManageFilters,
    columnDefs,
  });

  const {
    layoutChanged,
    onFilterChanged: agGridLayoutOnFilterChanged,
    onSaveLayout,
    resetMenuItems,
  } = useAgGridLayout({
    gridApi,
    columnDefs: columnDefsWithDefaultFilterOptions,
    hasLayoutActions,
    layoutType,
    initialState,
    isDataLoadCompleted: true,
    defaultFilterModel,
  });

  const onFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      if (applyTrimmedTextFilterModel(event.api)) {
        return;
      }

      saveFilterState(event);

      agGridLayoutOnFilterChanged && agGridLayoutOnFilterChanged(event);
    },
    [agGridLayoutOnFilterChanged, saveFilterState]
  );

  const defaultStatusBar = useMemo(
    () => ({
      statusPanels: [{ statusPanel: ServerSideTotalRowCount, align: 'right' }],
    }),
    []
  );

  const handleUpdateRows = useCallback(
    (rowDataTransaction: RowDataTransaction, options?: AgGridTransactionOptions) => {
      const resultTransaction = gridApi?.applyServerSideTransaction(rowDataTransaction);

      const rowData: AgGridDataRow[] = [];
      gridApi?.forEachNode(({ data }) => rowData.push(data));

      resultTransaction?.update?.forEach((row) => {
        rowData[row.rowIndex] = row.data;
      });

      let lastAddedRowIndex = null;
      resultTransaction?.add?.forEach((row) => {
        rowData.splice(row.rowIndex, 0, row.data);

        lastAddedRowIndex = row.rowIndex;
      });

      resultTransaction?.remove?.forEach((row) => {
        rowData.splice(row.rowIndex, 1);
      });

      onRowsUpdated({
        goToRowIndex: rowDataTransaction.addIndex || lastAddedRowIndex,
        ...options,
      });
    },
    [gridApi, onRowsUpdated]
  );

  const handleOnCellValueChanged = useCallback(
    async (event: CellValueChangedEvent) => {
      if (!onCellValueChanged) {
        return;
      }

      try {
        setUpdating(event.node.id, event.colDef.colId, true);

        const { updatedRow, removedRow, options } =
          onCellValueChanged && (await onCellValueChanged(gridRef.current, event));

        updatedRow &&
          handleUpdateRows(
            { update: [{ ...event.data, ...updatedRow }] },
            { refreshCells: true, redrawRows: true, ...options }
          );

        removedRow &&
          handleUpdateRows(
            { remove: [removedRow] },
            { refreshCells: true, redrawRows: true, ...options }
          );
      } catch (error) {
        const revertedRow = {
          ...event.data,
          [event.colDef.field]: event.oldValue,
        };

        handleUpdateRows({ update: [revertedRow] }, { refreshCells: true, redrawRows: true });
      } finally {
        setUpdating(event.node.id, event.colDef.colId, false);
      }
    },
    [onCellValueChanged, handleUpdateRows, setUpdating]
  );

  const statusBar = useMemo(
    () => ({
      statusPanels: [
        ...(customStatusBar?.statusPanels ?? []),
        ...(hasStatusBar ? defaultStatusBar?.statusPanels ?? [] : []),
      ],
    }),
    [customStatusBar, defaultStatusBar, hasStatusBar]
  );

  const context = useMemo(
    () => ({ tableId, entityType, totals, ...extraContext }),
    [tableId, entityType, totals, extraContext]
  );

  const rowSelection: RowSelectionOptions<unknown, unknown> = useMemo(
    () => ({
      headerCheckbox,
      checkboxes: hasRowSelection,
      mode: selectionMode,
      enableClickSelection,
      ...(isRowSelectable && { isRowSelectable }),
    }),
    [headerCheckbox, hasRowSelection, selectionMode, enableClickSelection, isRowSelectable]
  );

  const gridOptions = useGetGridOptions({
    rowModelType: 'serverSide' as RowModelType,
    getRowId: useCallback(({ data }) => data?.id?.toString(), []),
    columnDefs: columnDefsWithDefaultFilterOptions,
    defaultColDef,
    initialState,
    pageSize,
    cacheBlockSize: pageSize,
    serverSideDatasource,
    statusBar,
    onSelectionChanged,
    onFilterChanged,
    onCellValueChanged: handleOnCellValueChanged,
    reactiveCustomComponents: true,
    context,
    localeText: {
      loadingError: t('errorTitleDataLoading'),
    },
    rowSelection,
    selectionColumnDef,
    defaultCsvExportParams,
    defaultExcelExportParams,
    ...rest,
  });

  return {
    tableId,
    entityType,
    gridRef,
    gridApi,
    gridOptions,
    selectedRows,
    updateRows: handleUpdateRows,
    layoutType,
    hasLayoutActions,
    hasManageFilters,
    layoutChanged,
    onSaveLayout,
    resetMenuItems,
    hasQuickSearch,
    hasTopActions,
    deselectAllRows,
    context,
  };
};

export const [AgGridServerTableProviderWithoutCommonProvider, useAgGridServerTableContext] =
  constate(useAgGridServerTable);

export const AgGridServerTableProvider = ({
  children,
  ...props
}: IProps & { children: ReactNode }) => {
  return (
    <AgGridTableProvider {...props}>
      <AgGridServerTableProviderWithoutCommonProvider {...props}>
        {children}
      </AgGridServerTableProviderWithoutCommonProvider>
    </AgGridTableProvider>
  );
};
